import React, { useState } from 'react';
import { SavedCompanyModel } from './saved-company.models';
import { AppRoutes } from '../routes';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import './saved-company.scss';
import { companyService } from './company.service';
import { useDispatch, useSelector } from 'react-redux';
import { savedCompanyLoadedAction, savedCompanyLoadingAction } from './saved-company.actions';
import { authenticationService } from '../auth/authentication.service';
import { authenticationSuccessAction } from '../auth/authentication.actions';
import { gravityUseAsSelector } from '../auth/authentication.selectors';
import { workspaceService } from '../workspace-settings/workspace.service';
import { workspaceLoadedSuccessfulAction } from '../workspace-settings/workspace.actions';
import {
    financialReportService,
    INCOME_STATEMENT_PRICE,
    BALANCE_SHEET_PRICE,
    VALUATION_PRICE,
    FinancialReport,
    FinancialReportType,
} from './financial-report.service';
import { valuationService } from './valuation.service';
import { FinancialReportModal } from './FinancialReportModal.component';
import { popupSetModelAction } from '../popup/popup.actions';
import { popupService } from '../popup/popup.service';
import { PopupDialogType } from '../popup/popup.models';

export interface SavedCompanyDescriptionProps {
    savedCompany: SavedCompanyModel;
}

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 80;

const SavedCompanyDescriptionComponent: React.FC<SavedCompanyDescriptionProps & RouteComponentProps> = ({ savedCompany, history }) => {
    const dispatch = useDispatch();
    const useGravityAs = useSelector(gravityUseAsSelector);
    const [activeReport, setActiveReport] = useState<FinancialReport | null>(null);
    const [pendingType, setPendingType] = useState<FinancialReportType | 'Valuation' | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const refreshCreditBalance = async () => {
        try {
            if (useGravityAs) {
                const workspace = await workspaceService.getWorkspaceSettings(useGravityAs);
                dispatch(workspaceLoadedSuccessfulAction(workspace));
            } else {
                const user = await authenticationService.getUser();
                dispatch(authenticationSuccessAction(user));
            }
        } catch {
            // best-effort refresh; navbar will catch up on next navigation
        }
    };

    const goToEditPage = () => {
        history.push(AppRoutes.EditCompany(savedCompany.id.toString()));
    };

    const exportCsv = async (_e: any) => {
        dispatch(savedCompanyLoadingAction());
        await companyService.exportSavedCompany(savedCompany.id);
        dispatch(savedCompanyLoadedAction(savedCompany));
    };

    const pollReport = async (reportId: number): Promise<FinancialReport | null> => {
        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
            const response = await financialReportService.getReport(reportId);
            if (response?.status === 200) {
                const report = response.data;
                if (report.status === 'Completed' || report.status === 'Failed' || report.status === 'Refunded') {
                    return report;
                }
            }
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
        return null;
    };

    const handleReportPurchase = async (type: FinancialReportType) => {
        if (pendingType) return;

        setErrorMessage(null);
        setPendingType(type);

        try {
            const response = type === 'IncomeStatement'
                ? await financialReportService.purchaseIncomeStatement(savedCompany.companyId)
                : await financialReportService.purchaseBalanceSheet(savedCompany.companyId);

            refreshCreditBalance();

            const pendingReport: FinancialReport = {
                id: response.reportId,
                companyId: savedCompany.companyId,
                type,
                status: 'Pending',
                creditsSpent: response.creditsSpent,
                createdAt: Date.now() * 10000,
                completedAt: null,
                dataJson: null,
                errorMessage: null,
                externalJobId: null,
            };
            setActiveReport(pendingReport);

            const finalReport = await pollReport(response.reportId);
            if (finalReport) {
                setActiveReport(finalReport);
            } else {
                setActiveReport({ ...pendingReport, status: 'Failed', errorMessage: 'Timed out waiting for FinnAI to respond.' });
            }
        } catch (err: any) {
            const message = err?.response?.data?.errors?.[0] || err?.response?.data || err?.message || 'Could not start report.';
            setErrorMessage(typeof message === 'string' ? message : 'Could not start report.');
        } finally {
            setPendingType(null);
        }
    };

    const handleValuationPurchase = async () => {
        if (pendingType) return;

        setErrorMessage(null);
        setPendingType('Valuation');

        try {
            const response = await valuationService.purchaseValuation(savedCompany.companyId);
            refreshCreditBalance();
            history.push(`/valuation/${response.valuationId}`);
        } catch (err: any) {
            const message = err?.response?.data?.errors?.[0] || err?.response?.data || err?.message || 'Could not start valuation.';
            setErrorMessage(typeof message === 'string' ? message : 'Could not start valuation.');
        } finally {
            setPendingType(null);
        }
    };

    const confirmAndRun = (label: string, price: number, onConfirm: () => void) => {
        dispatch(popupSetModelAction({
            title: `Generate ${label}`,
            text: `This will charge ${price.toLocaleString('en-US')} credits to generate a ${label} for ${savedCompany.summary.fullName.valueDescription}. If the report fails, your credits are refunded automatically.`,
            type: PopupDialogType.Dialog,
            okButtonText: `Pay ${price.toLocaleString('en-US')} credits`,
            okButtonType: 'secondary',
            noButtonText: 'Cancel',
            okButtonAction: (evt: any) => {
                evt.preventDefault();
                evt.stopPropagation();
                popupService.hidePopup();
                onConfirm();
            },
            cancelButtonAction: (evt: any) => {
                evt.preventDefault();
                evt.stopPropagation();
                popupService.hidePopup();
            },
        }));
        popupService.showPopup();
    };

    const annualRevenue = savedCompany.financials.sort((a, b) => a.year > b.year ? -1 : 1)[0];

    const industrySpecified = savedCompany.summary.industry !== null;
    const industryPurchased = savedCompany.summary.industry?.value !== "";

    const shortNameSpecified = savedCompany.summary.shortName !== null;
    const shortNamePurchased = savedCompany.summary.shortName?.value !== "";

    // We only have registry connectors for some countries, and the two sets differ (valuation
    // reaches more jurisdictions than FinnAI's native agents). The API decides — see
    // CompanyDataCoverage on the backend — and the buttons simply follow, so we never offer
    // a purchase that has no data source behind it.
    //
    // `!== false` on purpose, NOT `=== true`: only an explicit false hides a button. A missing
    // flag means the API predates the gate, and hiding on absent would blank all three buttons
    // for EVERY company the moment this bundle ships ahead of the backend — a total blackout
    // that is indistinguishable from the gate working correctly. Absent therefore falls back to
    // the old always-show behaviour, which the (equally old) backend still handles as before.
    const showFinancialReports = savedCompany.financialReportsAvailable !== false;
    const showValuation = savedCompany.valuationAvailable !== false;

    const renderActionButtons = () => (
        <div className="actions stack stack-xs jc-fe ai-fe">
            <button className="button button--secondary" onClick={() => goToEditPage()}>
                <span className="button__inner">
                    <span>Add or Modify</span>
                </span>
            </button>
            <button className="button button--secondary" onClick={(e) => exportCsv(e)}>
                <span className="button__inner">
                    <span>Export</span>
                </span>
            </button>
            {showFinancialReports && (
                <button
                    className="button button--secondary"
                    disabled={pendingType !== null}
                    onClick={() => confirmAndRun('Income Statement', INCOME_STATEMENT_PRICE, () => handleReportPurchase('IncomeStatement'))}
                >
                    <span className="button__inner">
                        <span>
                            Income Statement
                            <span className="button__price"> · {INCOME_STATEMENT_PRICE} credits</span>
                        </span>
                    </span>
                </button>
            )}
            {showFinancialReports && (
                <button
                    className="button button--secondary"
                    disabled={pendingType !== null}
                    onClick={() => confirmAndRun('Balance Sheet', BALANCE_SHEET_PRICE, () => handleReportPurchase('BalanceSheet'))}
                >
                    <span className="button__inner">
                        <span>
                            Balance Sheet
                            <span className="button__price"> · {BALANCE_SHEET_PRICE} credits</span>
                        </span>
                    </span>
                </button>
            )}
            {showValuation && (
                <button
                    className="button"
                    disabled={pendingType !== null}
                    onClick={() => confirmAndRun('Valuation', VALUATION_PRICE, handleValuationPurchase)}
                >
                    <span className="button__inner">
                        <span>
                            Valuation
                            <span className="button__price"> · {VALUATION_PRICE.toLocaleString('en-US')} credits / €450</span>
                        </span>
                    </span>
                </button>
            )}
        </div>
    );

    return (
        <div className="company-details">
            <div className="h-stack h-stack-m jc-sb m-fd-c">
                <div className="stack stack-xs">
                    <h1 className="title title-l">{savedCompany.summary.fullName.valueDescription}</h1>
                    {
                        industrySpecified ?
                            industryPurchased ?
                                <ul className="h-stack h-stack-xs">
                                    <li>
                                        <span className="chip">
                                            <span className="label"> {savedCompany.summary.industry.valueDescription}</span>
                                        </span>
                                    </li>
                                </ul> : ""

                            :
                            <ul className="h-stack h-stack-xs">
                                <li>
                                    <span className="chip">
                                        <span className="label">Industry not specified</span>
                                    </span>
                                </li>
                            </ul>
                    }
                    {
                        shortNameSpecified ?
                            shortNamePurchased ?
                            <p className="title title-s">{savedCompany.summary.shortName?.valueDescription}</p> : ""

                            :
                            <p className="title title-s">Short Name not specified</p>
                    }

                    {savedCompany.summary.introduction !== null ?
                        <p>{savedCompany.summary.introduction.valueDescription}</p> : <></>
                    }
                    <span className="icon-label icon-label--label-right">
                        <span className="icon-label__inner">
                            <svg className="icon icon--m">
                                <use xlinkHref="#icon-location" />
                            </svg>
                            <span className="label">{savedCompany.summary.countryOfFoundation.valueDescription}</span>
                        </span>
                    </span>
                    {
                        annualRevenue?.incomeStatementTotalNetRevenues?.valueDescription ?
                            <span className="icon-label icon-label--label-right">
                                <span
                                    className="icon-label__inner">
                                    <svg className="icon icon--m">
                                        <use xlinkHref="#icon-revenue" />
                                    </svg>
                                    <span className="label">{annualRevenue.incomeStatementTotalNetRevenues?.valueDescription.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} EUR annual revenue ({annualRevenue?.year})</span>
                                </span>
                            </span> :
                            <span className="icon-label icon-label--label-right">
                                <span
                                    className="icon-label__inner">
                                    <svg className="icon icon--m">
                                        <use xlinkHref="#icon-revenue" />
                                    </svg>
                                    <span className="label">Annual revenues not specified</span>
                                </span>
                            </span>
                    }
                </div>

                <div className="actions stack stack-xs jc-fe ai-fe">
                    {
                        savedCompany.isBlocked ?
                            <div className="actions stack stack-xs jc-fe ai-fe">
                                <div className="locked-company">
                                    <h1 className="bold">Locked company</h1>
                                    <h2>This profile cannot be modified upon the request of the uploaded company.</h2>
                                </div>
                                <button className="button button--secondary" onClick={(e) => exportCsv(e)} >
                                    <span className="button__inner">
                                        <span>Export</span>
                                    </span>
                                </button>
                            </div> :
                            renderActionButtons()
                    }
                </div>
            </div>

            {errorMessage && (
                <p className="company-details__error">{errorMessage}</p>
            )}

            {pendingType !== null && pendingType !== 'Valuation' && activeReport === null && (
                <p className="company-details__hint">Starting {pendingType === 'IncomeStatement' ? 'Income Statement' : 'Balance Sheet'}…</p>
            )}

            {pendingType === 'Valuation' && (
                <p className="company-details__hint">Starting valuation…</p>
            )}

            {activeReport && (
                <FinancialReportModal report={activeReport} onClose={() => setActiveReport(null)} />
            )}
        </div >
    );
};

export const SavedCompanyDescription = withRouter(SavedCompanyDescriptionComponent);
